import {
  getCheckoutIdCookie,
  removeCheckoutIdCookie,
} from "@/services/cookie";
import { logInfo, logWarn } from "@/services/logging/logger";

const SESSION_STORAGE_KEY = "tamara-checkout-id";

/**
 * Maximum length for a checkout ID.
 * UUIDs are 36 chars; allowing extra headroom for any server-side format changes.
 */
const MAX_CHECKOUT_ID_LENGTH = 128;

/**
 * Allowed characters: alphanumeric, hyphens, and underscores.
 * Prevents injection of scripts, HTML entities, or path traversal via the URL param.
 * PCI DSS Req 6.2.4 — validate all input before use.
 */
const CHECKOUT_ID_PATTERN = /^[a-zA-Z0-9_-]+$/;

let resolvedCheckoutId: string | undefined;

export function isValidCheckoutId(
  value: string | null | undefined,
): value is string {
  if (!value) return false;
  if (value.length > MAX_CHECKOUT_ID_LENGTH) return false;
  return CHECKOUT_ID_PATTERN.test(value);
}

/** Mount prefixes that must never be treated as a path checkoutId. */
const HANDSHAKE_PATH_ID_BLOCKLIST = new Set(["v2", "checkout"]);

/**
 * Pulls a checkoutId out of a legacy path-shaped handshake URL:
 *   /{prefix?}/{checkoutId}/handshake
 * The current entry URL is `/handshake?checkoutId=…` (query-only); this helper
 * remains for any in-flight links during the cutover.
 */
export function extractCheckoutIdFromHandshakePath(
  pathname: string,
): string | undefined {
  const match = pathname.match(/\/([^/]+)\/handshake\/?$/);
  if (!match?.[1]) return undefined;
  if (HANDSHAKE_PATH_ID_BLOCKLIST.has(match[1])) return undefined;
  return sanitize(match[1]);
}

function sanitize(value: string | null | undefined): string | undefined {
  if (!isValidCheckoutId(value)) return undefined;
  return value;
}

function getCheckoutIdFromSessionStorage(): string | undefined {
  try {
    return sessionStorage.getItem(SESSION_STORAGE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

function persistCheckoutIdToSessionStorage(checkoutId: string): void {
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, checkoutId);
  } catch {
    // sessionStorage might be blocked in some environments
  }
}

/**
 * Ensures the checkoutId is present in the current URL without triggering navigation.
 * Uses replaceState to update the URL in-place.
 */
function ensureCheckoutIdInUrl(checkoutId: string): void {
  const url = new URL(window.location.href);
  if (url.searchParams.get("checkoutId") !== checkoutId) {
    url.searchParams.set("checkoutId", checkoutId);
    window.history.replaceState(window.history.state, "", url.toString());
  }
}

/**
 * Seeds the module-level checkoutId into sessionStorage + the query string.
 * Used by the handshake page so path-param ids become the shared source of truth
 * for the rest of the app (and for the hard-nav destinations after the dwell).
 */
export function seedCheckoutId(checkoutId: string): void {
  if (!isValidCheckoutId(checkoutId)) return;
  resolvedCheckoutId = checkoutId;
  persistCheckoutIdToSessionStorage(checkoutId);
  ensureCheckoutIdInUrl(checkoutId);
}

/**
 * Resolves the checkoutId from multiple sources with priority:
 *   1. Handshake path param (legacy `/…/{checkoutId}/handshake` entry URLs)
 *   2. URL query parameter (canonical for `/handshake?checkoutId=…` and all other routes)
 *   3. sessionStorage (survives same-tab reloads; resilient to cookie restrictions)
 *   4. Cookie (migration-only read for in-flight sessions from older deploys)
 *
 * Persists the resolved value to sessionStorage + URL. The cookie is never
 * written and is actively deleted to avoid stale duplicates.
 *
 * Must be called once at app startup before any API calls.
 */
export function initCheckoutId(): string | undefined {
  const rawFromPath = extractCheckoutIdFromHandshakePath(
    window.location.pathname,
  );
  const rawFromUrl = new URLSearchParams(window.location.search).get(
    "checkoutId",
  );
  const rawFromSessionStorage = getCheckoutIdFromSessionStorage();
  const rawFromCookie = getCheckoutIdCookie(); // migration-only read

  const fromPath = sanitize(rawFromPath);
  const fromUrl = sanitize(rawFromUrl);
  const fromSessionStorage = sanitize(rawFromSessionStorage);
  const fromCookie = sanitize(rawFromCookie);

  // Path wins on handshake entry so a stale/mismatched ?checkoutId= cannot
  // poison Cosmos/Statsig for the welcome dwell.
  resolvedCheckoutId =
    fromPath ?? fromUrl ?? fromSessionStorage ?? fromCookie ?? undefined;

  if (resolvedCheckoutId) {
    persistCheckoutIdToSessionStorage(resolvedCheckoutId);
    ensureCheckoutIdInUrl(resolvedCheckoutId);

    // Clean up the legacy cookie — URL + sessionStorage are the only stores now.
    // We still READ from the cookie above (migration fallback for in-flight
    // sessions that were started before this change), but never write to it.
    removeCheckoutIdCookie();

    const source = fromPath
      ? "handshake-path"
      : fromUrl
        ? "url"
        : fromSessionStorage
          ? "sessionStorage"
          : "cookie(migration)";

    logInfo("checkout-id", `Checkout ID resolved from ${source}`, {
      source,
      checkoutId: resolvedCheckoutId,
    });
  } else {
    const hadInvalidInput =
      (rawFromUrl !== null && !fromUrl) ||
      (rawFromCookie !== undefined && !fromCookie) ||
      (rawFromSessionStorage !== undefined && !fromSessionStorage);

    logWarn("checkout-id", "No checkout ID found in any source", {
      urlParam: rawFromUrl,
      cookie: rawFromCookie ? "(present)" : undefined,
      sessionStorage: rawFromSessionStorage ? "(present)" : undefined,
      rejectedByValidation: hadInvalidInput,
    });
  }

  return resolvedCheckoutId;
}

/**
 * Returns the checkout ID that was resolved during {@link initCheckoutId}.
 */
export function getResolvedCheckoutId(): string | undefined {
  return resolvedCheckoutId;
}

/**
 * Appends the checkoutId query parameter to a navigation path.
 * Preserves any existing query parameters already on the path.
 *
 * Use this whenever calling React Router's `navigate()` so the
 * checkoutId stays in the URL across all route transitions.
 *
 * @example
 *   navigate(withCheckoutId("/confirmation"), { replace: true });
 *   // → "/confirmation?checkoutId=abc-123"
 */
/**
 * Detects at runtime whether the app is running under the /v2 subpath
 * (e.g. checkout.tamara.co/v2/) and prepends it to a router-relative path.
 * For hard navigations (window.location.href) that bypass React Router's
 * automatic basename handling.
 */
export function toAppPath(routerPath: string): string {
  const prefix = window.location.pathname.startsWith("/v2") ? "/v2" : "";
  return `${prefix}${routerPath}`;
}

export function withCheckoutId(path: string): string {
  const checkoutId = resolvedCheckoutId;
  if (!checkoutId) return path;

  const url = new URL(path, window.location.origin);
  url.searchParams.set("checkoutId", checkoutId);

  const currentLocale = new URLSearchParams(window.location.search).get(
    "locale",
  );
  if (currentLocale && !url.searchParams.has("locale")) {
    url.searchParams.set("locale", currentLocale);
  }

  const result = `${url.pathname}${url.search}`;
  return url.hash ? `${result}${url.hash}` : result;
}
